import { call, put, select, takeEvery } from 'redux-saga/effects';

import * as authSelectors from '@redux/reducers/auth';
import API_BASE_URL from '@redux/sagas/settings/apibaseurl';
import { store } from '@redux/storeConfig/store';
import * as alerts from '@redux/utils/alerts';
import camelcaseKeys from 'camelcase-keys';
import forEach from 'lodash/forEach';
import groupBy from 'lodash/groupBy';
import { normalize } from 'normalizr';
import * as selectors from 'views/screens/Organizations/reducers';
import { appIntl } from '../../../../utility/context/IntlGlobalProvider';
import * as entitySelectors from '../../Entities/reducers';
import * as quotationActions from '../../Quotations/actions';
import * as actions from '../actions';
import * as schemas from '../schemas';
import {
  deleteOrganizationService,
  getOrganizationsListService,
  saveWorkplanOrderConfigurationService,
  saveRouteConfigurationService,
  quickCreateOrganizationService,
  getOrganizationApiKeysService,
  generateOrganizationApiKeyService,
  deactivateOrganizationApiKeyService,
  updateApiKeyDescriptionService,
  getOrganizationMcpKeysService,
  generateOrganizationMcpKeyService,
  deactivateOrganizationMcpKeyService,
  updateMcpKeyDescriptionService
} from '../services';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchOrganizationsList(action) {
  getOrganizationsListService(
    {},
    {
      successAction: (response) => {
        const resultData = response.data.organizationList.map((result) => camelcaseKeys(result));
        var usersData = response.data.usersList.map((result) => camelcaseKeys(result));
        var orderStatusIds = response.data.orderStatusIdList.map((result) => camelcaseKeys(result));
        const {
          entities: { organizations },
          result //order
        } = normalize(resultData, schemas.organizations); //normalize data to byId and order
        const usersByOrganization = groupBy(usersData, 'organizationId');
        const orderStatusByOrganization = groupBy(orderStatusIds, 'organizationId');
        forEach(organizations, (organization) => {
          if (organization.organizationId in usersByOrganization) {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                sacUsers: usersByOrganization[organization.organizationId]
              };
            }
          } else {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                sacUsers: []
              };
            }
          }
          if (organization.organizationId in orderStatusByOrganization) {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                orderStatusIds: orderStatusByOrganization[organization.organizationId]
              };
            }
          } else {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                orderStatusIds: []
              };
            }
          }
        });
        store.dispatch(actions.completeFetchingOrganizations(organizations, result));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingOrganizations(error));
      }
    }
  );
}

export function* watchFetchOrganizationsList() {
  yield takeEvery(types.ORGANIZATIONS_FETCH_STARTED, fetchOrganizationsList);
}
/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchOrganizationsList(action) {
  const entityTypeId = action.payload;
  const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
  getOrganizationsListService(
    { userFetchDate: date },
    {
      successAction: (response) => {
        const resultData = response.data.organizationList.map((result) => camelcaseKeys(result));
        var usersData = response.data.usersList.map((result) => camelcaseKeys(result));
        const {
          entities: { organizations },
          result //order
        } = normalize(resultData, schemas.organizations); //normalize data to byId and order
        const usersByOrganization = groupBy(usersData, 'organizationId');
        forEach(organizations, (organization) => {
          if (organization.organizationId in usersByOrganization) {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                sacUsers: usersByOrganization[organization.organizationId]
              };
            }
          } else {
            if (organizations) {
              organizations[organization.organizationId] = {
                ...organizations[organization.organizationId],
                sacUsers: []
              };
            }
          }
        });
        store.dispatch(actions.completeFetchingOrganizations(organizations, result));
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingOrganizations(error));
      }
    }
  );
}

export function* watchPartialFetchOrganizationsList() {
  yield takeEvery(types.ORGANIZATIONS_PARTIAL_FETCH_STARTED, partialFetchOrganizationsList);
}

/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addOrganization(action) {
  const { organization, isSelected } = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/save`, {
        method: 'POST',
        body: JSON.stringify({ ...organization, organizationId: null }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const addedOrganization = camelcaseKeys(jsonResult.data.organizationData);

        // const organizationContacts = camelcaseKeys(jsonResult.data.contacts);
        // const {
        //   entities: { contacts },
        //   result //order
        // } = normalize(organizationContacts, contactSchemas.contacts); //normalize data to byId and order
        // yield put(contactActions.completePartialFetchingContacts(contacts, result));

        yield put(
          actions.completeAddingOrganization(organization.organizationId, addedOrganization)
        );

        if (organization.quotationId)
          yield put(
            quotationActions.changeQuotationOrganization(
              {
                quotationId: organization.quotationId,
                baseQuotationId: organization.baseQuotationId
              },
              {
                organizationId: addedOrganization?.organizationId,
                organizationName: addedOrganization?.organizationName,
                organizationTaxNumber: addedOrganization?.taxNumber,
                organizationType: addedOrganization?.organizationType
              }
            )
          );

        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.organization'
          }),
          name: organization.organizationName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        // alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failAddingOrganization(organization.organizationId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAddingOrganization(organization.organizationId, error));
  }
}

export function* watchAddOrganizationsStarted() {
  yield takeEvery(types.ORGANIZATION_ADD_STARTED, addOrganization);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editOrganization(action) {
  const organization = action.payload;
  const { oldOrganization } = yield select((state) =>
    selectors.getOrganization(state, organization.organizationId)
  );
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/save`, {
        method: 'POST',
        body: JSON.stringify(organization),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const editedOrganization = camelcaseKeys(jsonResult.data.organizationData);

        // const organizationContacts = camelcaseKeys(jsonResult.data.contacts);
        // const {
        //   entities: { contacts },
        //   result //order
        // } = normalize(organizationContacts, contactSchemas.contacts); //normalize data to byId and order
        // yield put(contactActions.completePartialFetchingContacts(contacts, result));

        yield put(actions.completeEditingOrganization(editedOrganization));
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.organization'
          }),
          name: editedOrganization.organizationName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        yield put(actions.failEditingOrganization(oldOrganization, error));
        // alerts.showErrorAlertFailChanges({ error });
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };

    yield put(actions.failEditingOrganization(oldOrganization, error));
    // alerts.showErrorAlertFailChanges({ error });
  }
}

export function* watchEditOrganizationsStarted() {
  yield takeEvery(types.ORGANIZATION_EDIT_STARTED, editOrganization);
}

/* -------------------------------------------------------------------------- */
/*                        SAVE CONSOLIDABLE EVENTS                             */
/* -------------------------------------------------------------------------- */
function* saveConsolidableEvents(action) {
  const { organization, consolidableEvents } = action.payload;

  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);
      const response = yield call(fetch, `${API_BASE_URL}/organization/SaveConsolidableEvents`, {
        method: 'POST',
        body: JSON.stringify({ organizationId: organization.organizationId, consolidableEvents }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(
          actions.completeSavingConsolidableEvents(organization.organizationId, consolidableEvents)
        );
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({
            id: 'organizations.consolidableEventsSavingSuccess'
          })
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        const { oldOrganization } = yield select((state) =>
          selectors.getOrganization(state.organizations, organization.organizationId)
        );
        yield put(actions.failSavingConsolidableEvents(oldOrganization, error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };

    const { oldOrganization } = yield select((state) =>
      selectors.getOrganization(state.organizations, organization.organizationId)
    );
    yield put(actions.failSavingConsolidableEvents(oldOrganization, error));
    alerts.showErrorAlertFailChanges({ error });
  }
}

export function* watchSaveConsolidableEventsStarted() {
  yield takeEvery(types.ORGANIZATION_SAVE_CONSOLIDABLE_EVENTS_STARTED, saveConsolidableEvents);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeOrganization(action) {
  const { organizationId } = action.payload;
  const organization = yield select((state) => selectors.getOrganization(state, organizationId));
  deleteOrganizationService(
    { ...organization },
    {
      successAction: (response) => {
        store.dispatch(actions.completeRemovingOrganization(organizationId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failRemovingOrganization(organizationId, error));
      }
    }
  );
}

export function* watchRemoveOrganizationStarted() {
  yield takeEvery(types.ORGANIZATION_REMOVE_STARTED, removeOrganization);
}

/* -------------------------------------------------------------------------- */
/*                                 CODE CHECK                                 */
/* -------------------------------------------------------------------------- */
function* checkRegistrationCode(action) {
  const { organizationId, code } = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/validateRegistrationCode`, {
        method: 'POST',
        body: JSON.stringify({ code, organizationId }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const validCode = jsonResult.data.RegistrationCodeStatus === 1;
        yield put(actions.completeCheckingCode(validCode));
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
  }
}

export function* watchRegistrationCodeStarted() {
  yield takeEvery(types.ORGANIZATION_CODE_CHECK_STARTED, checkRegistrationCode);
}

/* -------------------------------------------------------------------------- */
/*                                 CODE ASSIGN                                */
/* -------------------------------------------------------------------------- */
function* assigCode(action) {
  const {
    operatorId,
    organizationId,
    organizationName,
    registrationCode,
    registrationMails,
    registrationDivisionId,
    registrationSubdivisionId,
    allowedApplications,
    autoIncrementJobsLicenses
  } = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/assignCode`, {
        method: 'POST',
        body: JSON.stringify({
          operatorId,
          organizationId,
          organizationName,
          registrationCode,
          registrationMails,
          registrationDivisionId,
          registrationSubdivisionId,
          allowedApplications,
          autoIncrementJobsLicenses
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(
          actions.completeAssigningCode(
            organizationId,
            registrationCode,
            allowedApplications,
            registrationDivisionId,
            registrationSubdivisionId,
            autoIncrementJobsLicenses
          )
        );
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.organization'
          }),
          name: organizationName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    // alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAssigningCode(error));
  }
}

export function* watchCodeAssignStarted() {
  yield takeEvery(types.ORGANIZATION_CODE_ASSIGN_STARTED, assigCode);
}

/* -------------------------------------------------------------------------- */
/*                            ADD DOWNTIME REASON                              */
/* -------------------------------------------------------------------------- */
function* addOrganizationDowntimeReason(action) {
  const downtimeReason = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/saveDowntimeReason`, {
        method: 'POST',
        body: JSON.stringify({ ...downtimeReason, reasonId: null }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const addedDowntimeReason = camelcaseKeys(jsonResult.data);
        yield put(
          actions.completeAddingOrganizationDowntimeReason(
            downtimeReason.reasonId,
            addedDowntimeReason
          )
        );

        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.downtimeReason'
          }),
          name: downtimeReason.reasonDescription,
          altText: true
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failAddingOrganizationDowntimeReason(downtimeReason.reasonId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failAddingOrganizationDowntimeReason(downtimeReason.reasonId, error));
  }
}

export function* watchAddOrganizationDowntimeReasonsStarted() {
  yield takeEvery(types.ORGANIZATION_DOWNTIME_REASON_ADD_STARTED, addOrganizationDowntimeReason);
}

/* -------------------------------------------------------------------------- */
/*                            EDIT DOWNTIME REASON                            */
/* -------------------------------------------------------------------------- */
function* editOrganizationDowntimeReason(action) {
  const downtimeReason = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/saveDowntimeReason`, {
        method: 'POST',
        body: JSON.stringify(downtimeReason),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const editedDowntimeReason = camelcaseKeys(jsonResult.data);
        yield put(actions.completeEditingOrganizationDowntimeReason(editedDowntimeReason));

        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.downtimeReason'
          }),
          name: downtimeReason.reasonDescription,
          altText: true
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failEditingOrganizationDowntimeReason(downtimeReason.reasonId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failEditingOrganizationDowntimeReason(downtimeReason.reasonId, error));
  }
}

export function* watchEditOrganizationDowntimeReasonsStarted() {
  yield takeEvery(types.ORGANIZATION_DOWNTIME_REASON_EDIT_STARTED, editOrganizationDowntimeReason);
}

/* -------------------------------------------------------------------------- */
/*                            REMOVE DOWNTIME REASON                          */
/* -------------------------------------------------------------------------- */
function* removeOrganizationDowntimeReason(action) {
  const downtimeReason = action.payload;
  try {
    const isAuth = yield select(authSelectors.isAuthenticated);

    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/deleteDowntimeReason`, {
        method: 'POST',
        body: JSON.stringify(downtimeReason),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        yield put(actions.completeRemovingOrganizationDowntimeReason(downtimeReason.reasonId));
        alerts.showSuccessDeletedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.downtimeReason'
          }),
          name: downtimeReason.reasonDescription
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        alerts.showErrorAlertFailChanges({ error });
        yield put(actions.failRemovingOrganizationDowntimeReason(downtimeReason.reasonId, error));
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    alerts.showErrorAlertFailChanges({ error });
    yield put(actions.failRemovingOrganizationDowntimeReason(downtimeReason.reasonId, error));
  }
}

export function* watchRemoveOrganizationDowntimeReasonsStarted() {
  yield takeEvery(
    types.ORGANIZATION_DOWNTIME_REASON_REMOVE_STARTED,
    removeOrganizationDowntimeReason
  );
}

/* -------------------------------------------------------------------------- */
/*                       WORKPLAN ORDERS CONFIGURATION                        */
/* -------------------------------------------------------------------------- */

function* editWorkplanOrdersConfiguration(action) {
  const { organization } = action.payload;
  const { oldOrganization } = yield select((state) =>
    selectors.getOrganization(state, organization.organizationId)
  );
  saveWorkplanOrderConfigurationService(
    {
      organizationId: organization.organizationId,
      organizationName: organization.organizationName,
      orderTag: organization.orderTag,
      orderVolumeUnit: organization.orderVolumeUnit,
      useWorkplanOrders: organization.useWorkplanOrders,
      workplanVisitValidationTime: organization.workplanVisitValidationTime,
      autoCreateWorkplanOnJobAssignment: organization.autoCreateWorkplanOnJobAssignment,
      orderStatusIds: organization.orderStatusIds
    },
    {
      successAction: (response) => {
        if (response.success) {
          const editedOrganization = camelcaseKeys(response.data.updatedOrganization);
          const orderStatusIdList = response.data.orderStatusIdList.map((result) =>
            camelcaseKeys(result)
          );

          store.dispatch(
            actions.completeUpdatingWorkplanOrdersConfiguration({
              ...editedOrganization,
              orderStatusIds: orderStatusIdList
            })
          );
          alerts.showSuccessMessage({
            success: appIntl().formatMessage({ id: 'workplans.orderConfigurationSuccess' }),
            timer: 5000
          });
        } else {
          alerts.showErrorAlertFailChanges({
            error: {
              errorMessage: appIntl().formatMessage({ id: 'workplans.orderConfigurationError' })
            }
          });
        }
      },
      errorAction: (error) => {
        store.dispatch(actions.failUpdatingWorkplanOrdersConfiguration(oldOrganization, error));
        alerts.showErrorAlertFailChanges({
          error: {
            errorMessage: appIntl().formatMessage({ id: 'workplans.orderConfigurationError' })
          }
        });
      }
    }
  );
}

export function* watchEditWorkplanOrdersConfiguration() {
  yield takeEvery(
    types.ORGANIZATION_WORKPLAN_ORDERS_CONFIGURATION_STARTED,
    editWorkplanOrdersConfiguration
  );
}

/* -------------------------------------------------------------------------- */
/*                           ROUTE CONFIGURATION                             */
/* -------------------------------------------------------------------------- */

function* editRouteConfiguration(action) {
  const { organization } = action.payload;
  const { oldOrganization } = yield select((state) =>
    selectors.getOrganization(state, organization.organizationId)
  );
  saveRouteConfigurationService(
    {
      organizationId: organization.organizationId,
      organizationName: organization.organizationName,
      useCustomRouteConfiguration: organization.useCustomRouteConfiguration,
      routeUnitOfflineTime: organization.routeUnitOfflineTime
    },
    {
      successAction: (response) => {
        if (response.success) {
          const editedOrganization = camelcaseKeys(response.data.updatedOrganization);

          store.dispatch(actions.completeUpdatingRouteConfiguration(editedOrganization));
          alerts.showSuccessMessage({
            success: appIntl().formatMessage({ id: 'routes.routeConfigurationSuccess' }),
            timer: 5000
          });
        } else {
          alerts.showErrorAlertFailChanges({
            error: {
              errorMessage: appIntl().formatMessage({ id: 'routes.routeConfigurationError' })
            }
          });
        }
      },
      errorAction: (error) => {
        store.dispatch(actions.failUpdatingRouteConfiguration(oldOrganization, error));
        alerts.showErrorAlertFailChanges({
          error: {
            errorMessage: appIntl().formatMessage({ id: 'routes.routeConfigurationError' })
          }
        });
      }
    }
  );
}

export function* watchEditRouteConfiguration() {
  yield takeEvery(types.ORGANIZATION_ROUTE_CONFIGURATION_STARTED, editRouteConfiguration);
}

/* -------------------------------------------------------------------------- */
/*                              QUICK CREATE                                  */
/* -------------------------------------------------------------------------- */
function* quickCreateOrganization(action) {
  const { organizationData } = action.payload;

  try {
    const isAuth = yield select(authSelectors.isAuthenticated);
    if (isAuth) {
      const token = yield select(authSelectors.getAuthToken);

      const response = yield call(fetch, `${API_BASE_URL}/organization/quickCreate`, {
        method: 'POST',
        body: JSON.stringify(organizationData),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      const jsonResult = yield response.json();
      if (response.status <= 300 && jsonResult.success) {
        const createdOrganization = camelcaseKeys(jsonResult.data.organizationData);

        yield put(actions.completeQuickCreateOrganization(createdOrganization));

        // Refresh organizations list
        yield put(actions.startFetchingOrganizations());

        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({
            id: 'organizations.organization'
          }),
          name: organizationData.organizationName
        });
      } else {
        const error = {
          errorMessage: jsonResult.message,
          errorNumber: jsonResult.number
        };
        yield put(actions.failQuickCreateOrganization(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  } catch (err) {
    const error = {
      errorMessage: err.toString(),
      errorNumber: -1
    };
    yield put(actions.failQuickCreateOrganization(error));
    alerts.showErrorAlertFailChanges({ error });
  }
}

export function* watchQuickCreateOrganization() {
  yield takeEvery(types.ORGANIZATION_QUICK_CREATE_STARTED, quickCreateOrganization);
}

/* -------------------------------------------------------------------------- */
/*                         ORGANIZATION API KEYS                              */
/* -------------------------------------------------------------------------- */
function* fetchOrgApiKeys(action) {
  const { organizationId } = action.payload;
  getOrganizationApiKeysService(
    { organizationId },
    {
      successAction: (response) => {
        const apiKeys = response.data.map((result) => camelcaseKeys(result));
        store.dispatch(actions.completeFetchingOrgApiKeys(apiKeys));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingOrgApiKeys(error));
      }
    }
  );
}

export function* watchFetchOrgApiKeys() {
  yield takeEvery(types.ORG_API_KEYS_FETCH_STARTED, fetchOrgApiKeys);
}

function* generateOrgApiKey(action) {
  const { organizationId, operatorId, description } = action.payload;
  generateOrganizationApiKeyService(
    { organizationId, operatorId, description },
    {
      successAction: (response) => {
        const apiKey = camelcaseKeys(response.data);
        store.dispatch(actions.completeGeneratingOrgApiKey(apiKey));
        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({ id: 'organizations.apiKey' }),
          name: description
        });
      },
      errorAction: (error) => {
        store.dispatch(actions.failGeneratingOrgApiKey(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchGenerateOrgApiKey() {
  yield takeEvery(types.ORG_API_KEY_GENERATE_STARTED, generateOrgApiKey);
}

function* deactivateOrgApiKey(action) {
  const { organizationApiKeyId } = action.payload;
  deactivateOrganizationApiKeyService(
    { organizationApiKeyId },
    {
      successAction: () => {
        store.dispatch(actions.completeDeactivatingOrgApiKey(organizationApiKeyId));
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({ id: 'organizations.apiKeyDeactivated' })
        });
      },
      errorAction: (error) => {
        store.dispatch(actions.failDeactivatingOrgApiKey(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchDeactivateOrgApiKey() {
  yield takeEvery(types.ORG_API_KEY_DEACTIVATE_STARTED, deactivateOrgApiKey);
}

function* updateApiKeyDescription(action) {
  const { organizationApiKeyId, newDescription } = action.payload;
  updateApiKeyDescriptionService(
    { organizationApiKeyId, newDescription },
    {
      successAction: () => {
        store.dispatch(
          actions.completeUpdatingApiKeyDescription(organizationApiKeyId, newDescription)
        );
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({ id: 'organizations.apiKey' }),
          name: newDescription
        });
      },
      errorAction: (error) => {
        store.dispatch(actions.failUpdatingApiKeyDescription(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchUpdateApiKeyDescription() {
  yield takeEvery(types.ORG_API_KEY_UPDATE_DESCRIPTION_STARTED, updateApiKeyDescription);
}

/* -------------------------------------------------------------------------- */
/*                          ORGANIZATION MCP KEYS                             */
/* -------------------------------------------------------------------------- */
function* fetchMcpKeys(action) {
  const { organizationId } = action.payload;
  getOrganizationMcpKeysService(
    { organizationId },
    {
      successAction: (response) => {
        const mcpKeys = response.data.map((result) => camelcaseKeys(result));
        store.dispatch(actions.completeFetchingMcpKeys(mcpKeys));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingMcpKeys(error));
      }
    }
  );
}

export function* watchFetchMcpKeys() {
  yield takeEvery(types.MCP_KEYS_FETCH_STARTED, fetchMcpKeys);
}

function* generateMcpKey(action) {
  const { organizationId, operatorId, description, allowWrites, rateLimitPerMinute, writeRateLimitPerMinute, dailyQuota } = action.payload;
  generateOrganizationMcpKeyService(
    { organizationId, operatorId, description, allowWrites, rateLimitPerMinute, writeRateLimitPerMinute, dailyQuota },
    {
      successAction: (response) => {
        const mcpKey = camelcaseKeys(response.data);
        store.dispatch(actions.completeGeneratingMcpKey(mcpKey));
        alerts.showSuccessCreatedMessage({
          object: appIntl().formatMessage({ id: 'organizations.mcpKey' }),
          name: description
        });
      },
      errorAction: (error) => {
        store.dispatch(actions.failGeneratingMcpKey(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchGenerateMcpKey() {
  yield takeEvery(types.MCP_KEY_GENERATE_STARTED, generateMcpKey);
}

function* deactivateMcpKey(action) {
  const { organizationMcpKeyId, organizationId } = action.payload;
  deactivateOrganizationMcpKeyService(
    { organizationMcpKeyId },
    {
      successAction: () => {
        store.dispatch(actions.completeDeactivatingMcpKey(organizationMcpKeyId));
        alerts.showSuccessMessage({
          success: appIntl().formatMessage({ id: 'organizations.mcpKeyDeactivated' })
        });
        // Re-fetch list to refresh
        store.dispatch(actions.startFetchingMcpKeys(organizationId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failDeactivatingMcpKey(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchDeactivateMcpKey() {
  yield takeEvery(types.MCP_KEY_DEACTIVATE_STARTED, deactivateMcpKey);
}

function* updateMcpKeyDescription(action) {
  const { organizationMcpKeyId, newDescription } = action.payload;
  updateMcpKeyDescriptionService(
    { organizationMcpKeyId, newDescription },
    {
      successAction: () => {
        store.dispatch(
          actions.completeUpdatingMcpKeyDescription(organizationMcpKeyId, newDescription)
        );
        alerts.showSuccessEditedMessage({
          object: appIntl().formatMessage({ id: 'organizations.mcpKey' }),
          name: newDescription
        });
      },
      errorAction: (error) => {
        store.dispatch(actions.failUpdatingMcpKeyDescription(error));
        alerts.showErrorAlertFailChanges({ error });
      }
    }
  );
}

export function* watchUpdateMcpKeyDescription() {
  yield takeEvery(types.MCP_KEY_UPDATE_DESCRIPTION_STARTED, updateMcpKeyDescription);
}
